import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Wallet, Plus, X, Trash2, ArrowRightLeft, TrendingUp, TrendingDown,
  Building2, CreditCard, Banknote, DollarSign, Loader2, Filter,
} from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none";
const money = (v) => `$${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

const ACCOUNT_TYPES = [
  { key: "checking", label: "Checking", icon: Building2, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "savings", label: "Savings", icon: Building2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "credit_card", label: "Credit card", icon: CreditCard, color: "bg-purple-50 text-purple-700 border-purple-200" },
  { key: "cash_box", label: "Cash box", icon: Banknote, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "other", label: "Other", icon: Wallet, color: "bg-slate-50 text-slate-700 border-slate-200" },
];

const PAYMENT_METHODS = ["card", "bank", "cash", "check", "ach", "zelle", "pix", "other"];

export default function Finance() {
  const [tab, setTab] = useState("cashbook");
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/finance/accounts"),
        api.get("/finance/summary"),
      ]);
      setAccounts(a); setSummary(s);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Wallet className="w-6 h-6" /> Finance
          </h1>
          <p className="hidden md:block text-xs text-slate-500 mt-0.5">
            Cash book, accounts, transfers, and per-project P&L.
          </p>
        </div>
      </div>

      {/* KPI summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard label="Total balance" value={money(summary.total_balance)} icon={Wallet} tone="text-slate-900" />
          <KPICard label="Cash in" value={money(summary.cash_in)} icon={TrendingUp} tone="text-emerald-700" />
          <KPICard label="Cash out" value={money(summary.cash_out)} icon={TrendingDown} tone="text-rose-700" />
          <KPICard label="Net" value={money(summary.net)} icon={DollarSign} tone={summary.net >= 0 ? "text-emerald-700" : "text-rose-700"} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 flex flex-wrap gap-x-4 gap-y-1 overflow-x-auto text-sm font-medium">
        {[
          { key: "cashbook", label: "Cash Book" },
          { key: "accounts", label: "Accounts" },
          { key: "projects", label: "Project P&L" },
        ].map((t) => (
          <button
            key={t.key} data-testid={`fin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 pb-2 px-1 -mb-px border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "cashbook" && <CashBookTab accounts={accounts} onChange={load} />}
      {tab === "accounts" && <AccountsTab accounts={accounts} onChange={load} />}
      {tab === "projects" && <ProjectPnLTab />}

      {busy && <div className="text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>}
    </div>
  );
}

function KPICard({ label, value, icon: Ic, tone = "text-slate-900" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
        <Ic className={`w-4 h-4 ${tone}`} />
      </div>
      <div className={`text-lg md:text-xl font-bold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}

// -------------------- Cash Book --------------------
function CashBookTab({ accounts, onChange }) {
  const [entries, setEntries] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [filter, setFilter] = useState({ account: "", category: "", allocation: "" });
  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.account) q.set("account_id", filter.account);
    if (filter.category) q.set("category", filter.category);
    if (filter.allocation) q.set("allocation", filter.allocation);
    const r = await api.get(`/finance/ledger?${q.toString()}`);
    setEntries(r);
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get("/projects").then((rs) => setProjects(rs || [])).catch(() => {});
    api.get("/tickets").then((rs) => setTickets(rs || [])).catch(() => {});
    api.get("/finance/categories").then(setCategories).catch(() => setCategories([]));
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.del(`/finance/ledger/${id}`);
      await load(); await onChange();
      toast.success("Entry removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="fin-new-entry-btn" onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-slate-800">
          <Plus className="w-4 h-4" /> New entry
        </button>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={filter.account} onChange={(e) => setFilter({ ...filter, account: e.target.value })}
            data-testid="fin-filter-account" className="text-xs rounded border border-slate-300 px-2 py-1">
            <option value="">All accounts</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={filter.allocation} onChange={(e) => setFilter({ ...filter, allocation: e.target.value })}
            data-testid="fin-filter-allocation" className="text-xs rounded border border-slate-300 px-2 py-1">
            <option value="">All allocations</option>
            <option value="project">Project</option>
            <option value="service_call">Service call</option>
            <option value="administrative">Administrative</option>
          </select>
          <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            data-testid="fin-filter-category" className="text-xs rounded border border-slate-300 px-2 py-1">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Account</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Alloc.</th>
                <th className="text-left px-3 py-2">Memo</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const acc = accounts.find((a) => a.id === e.account_id);
                return (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 tabular-nums text-slate-600">{e.date}</td>
                    <td className="px-3 py-2 text-slate-700">{acc?.name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{e.category}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                        e.allocation === "project" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : e.allocation === "service_call" ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-slate-50 text-slate-600 border-slate-200"
                      }`}>{e.allocation}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{e.memo || "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${e.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>
                      {e.direction === "in" ? "+" : "−"} {money(e.amount)}
                    </td>
                    <td className="px-1 py-2">
                      <button onClick={() => remove(e.id)} className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {entries.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No entries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {newOpen && (
        <EntryModal accounts={accounts} projects={projects} tickets={tickets} categories={categories}
          onClose={() => setNewOpen(false)}
          onSaved={async () => { setNewOpen(false); await load(); await onChange(); }} />
      )}
    </div>
  );
}

function EntryModal({ accounts, projects, tickets, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: today(), account_id: accounts[0]?.id || "", direction: "out",
    amount: 0, category: "materials", payment_method: "bank",
    memo: "", allocation: "administrative", project_id: "", ticket_id: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.account_id) return toast.error("Pick an account");
    if (!form.amount || form.amount <= 0) return toast.error("Amount must be > 0");
    setSaving(true);
    try {
      const payload = {
        date: form.date, account_id: form.account_id, direction: form.direction,
        amount: parseFloat(form.amount) || 0,
        category: form.category, payment_method: form.payment_method,
        memo: form.memo,
        project_id: form.allocation === "project" ? form.project_id : null,
        ticket_id: form.allocation === "service_call" ? form.ticket_id : null,
      };
      await api.post("/finance/ledger", payload);
      toast.success("Entry saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="fin-entry-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="font-semibold">New entry</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Direction</label>
              <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className={inputCls}>
                <option value="out">Cash out (expense)</option>
                <option value="in">Cash in (revenue)</option>
              </select>
            </div>
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Account</label>
            <select data-testid="fin-entry-account" value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} className={inputCls}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Amount ($)</label>
              <input data-testid="fin-entry-amount" type="number" step="0.01" min="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className={`${inputCls} text-right tabular-nums`} />
            </div>
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Method</label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls}>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Category</label>
            <input list="fin-cats" data-testid="fin-entry-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
            <datalist id="fin-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-500">Allocate to</label>
            <select data-testid="fin-entry-allocation" value={form.allocation}
              onChange={(e) => setForm({ ...form, allocation: e.target.value })} className={inputCls}>
              <option value="administrative">Administrative (default)</option>
              <option value="project">Project</option>
              <option value="service_call">Service call</option>
            </select>
            {form.allocation === "project" && (
              <select data-testid="fin-entry-project" value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={`${inputCls} mt-1`}>
                <option value="">— Pick a project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.number} · {p.name}</option>)}
              </select>
            )}
            {form.allocation === "service_call" && (
              <select data-testid="fin-entry-ticket" value={form.ticket_id}
                onChange={(e) => setForm({ ...form, ticket_id: e.target.value })} className={`${inputCls} mt-1`}>
                <option value="">— Pick a service call —</option>
                {tickets.map((t) => <option key={t.id} value={t.id}>{t.number || t.id} · {t.title || t.description || ""}</option>)}
              </select>
            )}
          </div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Memo</label>
            <input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} className={inputCls} placeholder="Optional notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
          <button data-testid="fin-entry-save" onClick={submit} disabled={saving}
            className="px-4 py-1.5 bg-slate-900 text-white rounded text-xs font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Save entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Accounts --------------------
function AccountsTab({ accounts, onChange }) {
  const [modal, setModal] = useState(null);       // null | 'new' | 'transfer'

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This only works if no ledger entries exist yet.`)) return;
    try {
      await api.del(`/finance/accounts/${a.id}`);
      toast.success("Account deleted"); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="fin-new-account-btn" onClick={() => setModal("new")} className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-slate-800">
          <Plus className="w-4 h-4" /> New account
        </button>
        <button data-testid="fin-transfer-btn" onClick={() => setModal("transfer")} disabled={accounts.length < 2}
          className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50">
          <ArrowRightLeft className="w-4 h-4" /> Transfer
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const meta = ACCOUNT_TYPES.find((x) => x.key === a.type) || ACCOUNT_TYPES[0];
          const Ic = meta.icon;
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`fin-account-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${meta.color}`}>
                    <Ic className="w-3 h-3" />{meta.label}
                  </div>
                  <div className="mt-2 font-semibold text-slate-900 truncate">{a.name}</div>
                  <div className="text-[11px] text-slate-500">{a.institution || "—"}{a.account_last4 ? ` · ****${a.account_last4}` : ""}</div>
                </div>
                <button onClick={() => remove(a)} className="p-1 hover:bg-red-50 rounded text-red-500 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[10px] font-bold uppercase text-slate-400">Current balance</div>
                <div className={`text-xl font-bold tabular-nums ${a.current_balance >= 0 ? "text-slate-900" : "text-rose-700"}`}>
                  {money(a.current_balance || 0)}
                </div>
              </div>
            </div>
          );
        })}
        {accounts.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400 bg-white border border-dashed border-slate-300 rounded-xl">
            No accounts yet — click "New account" to add your first bank account, cash box or credit card.
          </div>
        )}
      </div>

      {modal === "new" && <AccountModal onClose={() => setModal(null)} onSaved={() => { setModal(null); onChange(); }} />}
      {modal === "transfer" && <TransferModal accounts={accounts} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChange(); }} />}
    </div>
  );
}

function AccountModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", type: "checking", opening_balance: 0, institution: "", account_last4: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/finance/accounts", { ...form, opening_balance: parseFloat(form.opening_balance) || 0 });
      toast.success("Account created"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b"><h2 className="font-semibold">New account</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Name *</label>
            <input data-testid="fin-account-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Chase Business Checking" /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              {ACCOUNT_TYPES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Institution</label>
              <input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} className={inputCls} placeholder="Chase" /></div>
            <div><label className="text-[10px] font-bold uppercase text-slate-500">Last 4</label>
              <input maxLength={4} value={form.account_last4} onChange={(e) => setForm({ ...form, account_last4: e.target.value })} className={inputCls} placeholder="1234" /></div>
          </div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Opening balance ($)</label>
            <input type="number" step="0.01" value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              className={`${inputCls} text-right tabular-nums`} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
          <button data-testid="fin-account-save" onClick={submit} disabled={saving} className="px-4 py-1.5 bg-slate-900 text-white rounded text-xs font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferModal({ accounts, onClose, onSaved }) {
  const [form, setForm] = useState({ date: today(), from_account_id: accounts[0]?.id || "", to_account_id: accounts[1]?.id || "", amount: 0, memo: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.amount || form.amount <= 0) return toast.error("Amount > 0 required");
    if (form.from_account_id === form.to_account_id) return toast.error("From and to must differ");
    setSaving(true);
    try {
      await api.post("/finance/transfer", { ...form, amount: parseFloat(form.amount) });
      toast.success("Transfer posted"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b"><h2 className="font-semibold">Transfer between accounts</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Date</label>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">From</label>
            <select value={form.from_account_id} onChange={(e) => setForm({ ...form, from_account_id: e.target.value })} className={inputCls}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({money(a.current_balance || 0)})</option>)}
            </select></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">To</label>
            <select value={form.to_account_id} onChange={(e) => setForm({ ...form, to_account_id: e.target.value })} className={inputCls}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({money(a.current_balance || 0)})</option>)}
            </select></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Amount ($)</label>
            <input type="number" step="0.01" min="0" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className={`${inputCls} text-right tabular-nums`} /></div>
          <div><label className="text-[10px] font-bold uppercase text-slate-500">Memo</label>
            <input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} className={inputCls} placeholder="e.g. Pay CC monthly bill" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
          <button data-testid="fin-transfer-save" onClick={submit} disabled={saving} className="px-4 py-1.5 bg-slate-900 text-white rounded text-xs font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Post transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Project P&L --------------------
function ProjectPnLTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/finance/project-pnl").then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="fin-project-pnl">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Project</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Client</th>
              <th className="text-right px-3 py-2">Contract</th>
              <th className="text-right px-3 py-2">Invoiced</th>
              <th className="text-right px-3 py-2">Cash in</th>
              <th className="text-right px-3 py-2">Cash out</th>
              <th className="text-right px-3 py-2">Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2"><div className="text-[10px] text-slate-400 tabular-nums">{r.number}</div><div className="text-sm font-semibold text-slate-800">{r.name}</div></td>
                <td className="px-3 py-2 hidden md:table-cell text-slate-600">{r.client_name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-800">{money(r.contract)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{money(r.invoiced)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{money(r.cash_in)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700">{money(r.cash_out)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(r.net)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No projects yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
